import { useNavigate, useParams } from "react-router-dom";
import { gql } from "@apollo/client";
import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { useMutation, useQuery } from "@apollo/client/react";
import { Loader } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { modals } from "@mantine/modals";
import * as yup from "yup";
import { useEffect, useState } from "react";
import {
  CreateTournamentStepInput,
  StepStatus,
  StepType,
  TournamentStep,
} from "../../../../../gql/graphql";
import { CreationForm } from "../../../components/form/CreationForm";
import { StepTypeSelector } from "./StepTypeSelector";

export const StepForm = () => {
  const navigate = useNavigate();
  const { stepId, tournamentId } = useParams();
  const [selectedStepType, setSelectedStepType] = useState<StepType | null>(
    null,
  );

  const { data, loading } = useQuery(
    gql`
      query ($id: ID!) {
        tournamentStep(id: $id) {
          id
          name
          description
          type
          status
          order
        }
      }
    ` as TypedDocumentNode<{ tournamentStep: TournamentStep }>,
    {
      variables: {
        id: stepId,
      },
      skip: !stepId,
    },
  );

  useEffect(() => {
    if (data) {
      setSelectedStepType(data?.tournamentStep?.type);
    }
  }, [data]);

  const [createTournamentStep] = useMutation<{
    createTournamentStep: { id: string };
  }>(gql`
    mutation ($tournamentId: ID!, $step: CreateTournamentStepInput!) {
      createTournamentStep(tournamentId: $tournamentId, step: $step) {
        id
      }
    }
  `);

  const [updateTournamentStep] = useMutation<{
    updateTournamentStep: { id: string };
  }>(gql`
    mutation ($stepId: ID!, $step: CreateTournamentStepInput!) {
      updateTournamentStep(stepId: $stepId, step: $step) {
        id
      }
    }
  `);

  if (loading) return <Loader />;

  if (!selectedStepType) {
    return <StepTypeSelector onSelect={setSelectedStepType} />;
  }

  return (
    <CreationForm
      title="Tournament step"
      submitText={stepId ? "Update step" : "Create a new step"}
      schemaDefinition={{
        name: {
          type: "INPUT",
          inputType: "text",
          label: "Name",
          info: "The name of the step",
          yupConfig: yup.string().required(),
          defaultValue: data?.tournamentStep?.name,
        },
        description: {
          type: "INPUT",
          inputType: "text",
          label: "Description",
          info: "The description of the step",
          yupConfig: yup.string().required(),
          defaultValue: data?.tournamentStep?.description,
        },
        order: {
          type: "INPUT",
          inputType: "number",
          label: "Order",
          info: "The order of the step",
          yupConfig: yup.number().required(),
          defaultValue: data?.tournamentStep?.order,
        },
      }}
      onSubmit={async (values: any) => {
        const step: CreateTournamentStepInput = {
          name: values.name,
          description: values.description,
          order: values.order,
          type: selectedStepType,
        };

        if (stepId) {
          if (
            !(await new Promise((resolve) => {
              if (
                data?.tournamentStep?.status !== StepStatus.Configured &&
                data?.tournamentStep?.status !== StepStatus.Ended
              ) {
                modals.openConfirmModal({
                  title: "Warning",
                  id: "warning",
                  children:
                    "You are about to update a step that has already been generated. This will reset the step configuration and remove all your generated content. Are you sure you want to continue?",
                  confirmProps: { color: "red" },
                  labels: {
                    cancel: "Cancel",
                    confirm: "Continue",
                  },
                  centered: true,
                  onConfirm: () => {
                    resolve(true);
                  },
                  onClose: () => {
                    resolve(false);
                  },
                });
              } else if (data?.tournamentStep?.status === StepStatus.Ended) {
                notifications.show({
                  title: "Step ended",
                  message: "You cannot update a step that has ended",
                  color: "red",
                });
                resolve(false);
              } else {
                resolve(true);
              }
            }))
          ) {
            return;
          }

          await updateTournamentStep({
            variables: {
              stepId,
              step,
            },
          }).then((data) => {
            if (data?.error) {
              throw new Error(data.error.message);
            }
            return data.data?.updateTournamentStep.id;
          });
          notifications.show({
            title: "Step updated",
            message: "The step has been updated",
            color: "green",
          });
        } else {
          const createdStep = await createTournamentStep({
            variables: {
              tournamentId,
              step,
            },
          }).then((data) => {
            if (data?.error) {
              throw new Error(data.error.message);
            }
            return data.data?.createTournamentStep.id;
          });
          notifications.show({
            title: "Step created",
            message: "The step has been created",
            color: "green",
          });
          navigate(`../steps/${createdStep}`);
        }
      }}
    />
  );
};
